"use client"
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type FollowStatus } from '@/lib/api'
import { Button } from "@/components/ui/button"
import {
  Heart,
  LoaderCircle,
  Plus,
  HeartHandshake,
  HeartCrack
} from "lucide-react"
import { useToast } from '@/hooks/use-toast';
import { Tooltip, Popconfirm } from 'antd';
import { useIsLoggedIn } from '@/app/stores/useUserStore';
import { showLoginModal } from '@/components/login';

export function UserAttend({
  status,
  userId,
  className = '',
  size = 'sm',
  onChangeFollow,
  confirmDisabled = false,
}: {
  status?: FollowStatus,
  userId?: number | string,
  className?: string,
  size?: string,
  confirmDisabled?: boolean,
  onChangeFollow?: () => void,
}) {
  const { t } = useTranslation();
  // 是否登录
  const isLoggedIn = useIsLoggedIn();
  const { toast } = useToast();
  const [loading, setLoading] = useState<boolean>(false);
  // 更换关注状态
  const toggleFollow = async (followUserId?: number | string, status?: number) => {
    if (!isLoggedIn) {
      showLoginModal();
      return;
    }
    if (!followUserId) return;
    setLoading(true)
    try {
      const response = await api.follow.unFollow(Number(followUserId))
      if (response.code !== 200) {
        toast({
          title: t('action.tips'),
          description: status === 0 ? (response.msg || t('tips.followFail')) : t('tips.unfollowFail')
        })
      }
      else {
        toast({
          title: t('action.tips'),
          description: status === 0 ? t('tips.follow') : t('tips.unfollow')
        })
        onChangeFollow && onChangeFollow()
      }
    } catch (err) {
      console.error('取消关注失败:', err)
    } finally {
      setLoading(false)
    }
  };

  return (
    <>
      {status === 0 && (
        <Button
          size={size as any}
          variant="default"
          className={`px-2 w-[95px] max-md:w-[80px] max-md:text-xs ${className}`}
          disabled={loading}
          onClick={(e) => {
            e && e.stopPropagation();
            toggleFollow(userId, 0)
          }}
        >
          {loading ? <LoaderCircle className='animate-spin mr-1' /> : <Plus />}
          {t('follow.follow')}
        </Button>
      )}
      {status === 1 && (
        confirmDisabled ? (
          <Tooltip title={t('follow.unfollow')}>
            <Button
              size={size as any}
              variant="secondary"
              className={`!w-8 max-md:text-xs ${className} group`}
              disabled={loading}
              onClick={(e) => {
                e && e.stopPropagation();
                toggleFollow(userId, 1)
              }}
            >
              {loading ? <LoaderCircle className='animate-spin' /> : (
                <>
                  <Heart fill="currentColor" className='text-red-500 group-hover:hidden' />
                  <HeartCrack className='text-red-500 hidden group-hover:block' />
                </>
              )}
            </Button>
          </Tooltip>
        ) : (
          <Tooltip title={t('follow.unfollow')}>
            <Popconfirm
              title={t('follow.unfollow')}
              description={t('follow.confirmUnfollowDesc')}
              onConfirm={(e) => {
                e && e.stopPropagation();
                toggleFollow(userId, 1)
              }}
              okButtonProps={{
                className: 'bg-primary-500 hover:bg-primary-600 text-font-primary'
              }}
              okText={t('action.confirm')}
              disabled={loading}
              showCancel={false}
            >
              <Button
                size={size as any}
                variant="secondary"
                className={`!w-8 max-md:text-xs ${className} group`}
                disabled={loading}
              >
                {loading ? <LoaderCircle className='animate-spin' /> : (
                  <>
                    <Heart fill="currentColor" className='text-red-500 group-hover:hidden' />
                    <HeartCrack className='text-red-500 hidden group-hover:block' />
                  </>
                )}
              </Button>
            </Popconfirm>
          </Tooltip>
        )
      )}
      {status === 2 && (
        <Tooltip title={t('follow.mutualFollowTooltip')}>
          <Button
            size={size as any}
            variant="secondary"
            className={`!w-9 max-md:text-xs ${className} group`}
            disabled={loading}
            onClick={(e) => {
              e && e.stopPropagation();
              toggleFollow(userId, 2)
            }}
          >
            {loading ? <LoaderCircle className='animate-spin' /> : (
              <>
                <HeartHandshake className='text-red-500 group-hover:hidden' />
                <HeartCrack className='hidden group-hover:block' />
              </>
            )}
          </Button>
        </Tooltip>
      )}
    </>
  )
}